import type { Sensor } from '@si/types';
import { patch } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

interface Params {
  projectId: string;
  installationId: string;
  sensorId: number;
  editedSensor: Sensor;
}

export function useUpdateSensor() {
  const queryClient = useQueryClient();

  const { mutate, error, isPending, isSuccess, reset } = useMutation({
    mutationFn: (params: Params) =>
      patch({
        url: `/projects/${params.projectId}/sensors/${params.sensorId}`,
        body: params.editedSensor,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          if (!Array.isArray(queryKey) || queryKey[0] !== 'sensors') {
            return false;
          }

          const queryParams = queryKey[1] as { projectId?: string } | undefined;
          return queryParams?.projectId === variables.projectId;
        },
      });
    },
  });

  const updateSensor = useCallback((params: Params) => mutate(params), [mutate]);

  return {
    updateSensor,
    error,
    isPending,
    isSuccess,
    reset,
  };
}
